import { createAsyncThunk } from '@reduxjs/toolkit';
import api, { handleApiError } from 'state/api';

export const getSuppliersApi = createAsyncThunk(
  'suppliers/getSuppliersApi',
  async ({ page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await api.get(`suppliers?page=${page}&limit=${limit}`);
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      return handleApiError('getSuppliersApi', error, rejectWithValue);
    }
  },
);

export const getSupplierApi = createAsyncThunk('suppliers/getSupplier', async (id) => {
  const response = await api.get(`suppliers/${id}`);
  return response.data.success ? response.data.data : response.data;
});

export const updateSupplierApi = createAsyncThunk(
  'suppliers/updateSupplier',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`suppliers/${id}`, data);
      return response.data;
    } catch (error) {
      return handleApiError('updateSupplierApi', error, rejectWithValue);
    }
  },
);

export const getSuppliersListApi = createAsyncThunk(
  'suppliers/getSuppliersListApi',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('suppliers/list');
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      return handleApiError('getSuppliersListApi', error, rejectWithValue);
    }
  },
);

export const uploadShippingApi = createAsyncThunk(
  'suppliers/uploadShippingApi',
  async ({ id, file }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`suppliers/${id}/shipping/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      return handleApiError('uploadShippingApi', error, rejectWithValue);
    }
  },
);

export const downloadInventoryFileFormatApi = createAsyncThunk(
  'suppliers/downloadInventoryFileFormat',
  async (supplierId, { rejectWithValue }) => {
    try {
      const response = await api.get(`suppliers/${supplierId}/inventory/download-format`, {
        responseType: 'blob',
      });
      
      // Create download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'inventory-format.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return { message: 'File downloaded successfully' };
    } catch (error) {
      return handleApiError('downloadInventoryFileFormatApi', error, rejectWithValue);
    }
  },
);

export const getSupplierShippingPricesApi = createAsyncThunk(
  'suppliers/getSupplierShippingPricesApi',
  async ({ id, page = 1, limit = 50, search = '' }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (search && String(search).trim()) {
        params.set('search', String(search).trim());
      }
      const response = await api.get(`suppliers/${id}/shipping/prices?${params.toString()}`);
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      return handleApiError('getSupplierShippingPricesApi', error, rejectWithValue);
    }
  },
);

export const upsertSupplierShippingPriceApi = createAsyncThunk(
  'suppliers/upsertSupplierShippingPriceApi',
  async ({ id, sku, cost }, { rejectWithValue }) => {
    try {
      const response = await api.put(`suppliers/${id}/shipping/prices`, { sku, cost });
      return response.data;
    } catch (error) {
      return handleApiError('upsertSupplierShippingPriceApi', error, rejectWithValue);
    }
  },
);

export const downloadShippingFileFormatApi = createAsyncThunk(
  'suppliers/downloadShippingFileFormat',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('suppliers/shipping/download-format', {
        responseType: 'blob',
      });

      // Create download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'shipping-format.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { message: 'File downloaded successfully' };
    } catch (error) {
      return handleApiError('downloadShippingFileFormatApi', error, rejectWithValue);
    }
  },
);

// SFTP Connections (inventory-management service)
export const getSftpConnectionsApi = createAsyncThunk(
  'suppliers/getSftpConnectionsApi',
  async (activeOnly = false, { rejectWithValue }) => {
    try {
      const url = activeOnly
        ? 'inventory-management/sftp?activeOnly=true'
        : 'inventory-management/sftp';
      const response = await api.get(url);
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      return handleApiError('getSftpConnectionsApi', error, rejectWithValue);
    }
  },
);

export const getSftpConnectionApi = createAsyncThunk(
  'suppliers/getSftpConnectionApi',
  async ({ id, includePassword = false }, { rejectWithValue }) => {
    try {
      const params = includePassword ? '?includePassword=true' : '';
      const response = await api.get(`inventory-management/sftp/${id}${params}`);
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      return handleApiError('getSftpConnectionApi', error, rejectWithValue);
    }
  },
);

export const createSftpConnectionApi = createAsyncThunk(
  'suppliers/createSftpConnectionApi',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('inventory-management/sftp', data);
      return response.data;
    } catch (error) {
      return handleApiError('createSftpConnectionApi', error, rejectWithValue);
    }
  },
);

export const updateSftpConnectionApi = createAsyncThunk(
  'suppliers/updateSftpConnectionApi',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`inventory-management/sftp/${id}`, data);
      return response.data;
    } catch (error) {
      return handleApiError('updateSftpConnectionApi', error, rejectWithValue);
    }
  },
);

export const deleteSftpConnectionApi = createAsyncThunk(
  'suppliers/deleteSftpConnectionApi',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.delete(`inventory-management/sftp/${id}`);
      return response.data;
    } catch (error) {
      return handleApiError('deleteSftpConnectionApi', error, rejectWithValue);
    }
  },
);
